// ============================================================================
// Facebook Page — listPosts capability method.
//
// Reads existing Page posts so marketing can import posts that were published
// before the tenant started using Kubdee. This is intentionally read-only and
// provider-neutral at the capability boundary.
// ============================================================================

import type {
  SocialListPostsArgs,
  SocialListPostsResult,
  SocialPostingCallCtx,
  SocialProviderPost,
  SocialResult,
} from '../../capabilities/social-posting'
import type { RateLimitCheck } from './rate-limit-check'
import { errorFromResponse } from './errors'
import { FB_PAGE_GRAPH_API_BASE } from './oauth'
import type {
  FacebookPagePostRow,
  FacebookPagePostsResponse,
} from './types'

const ENDPOINT_LABEL = 'facebook:page_posts'
const DEFAULT_LIMIT = 25
const MAX_LIMIT = 100
const COMPAT_POST_FIELDS = [
  'id',
  'message',
  'created_time',
  'permalink_url',
  'is_published',
] as const

export interface ListPostsDeps {
  fetchImpl?: typeof fetch
  graphApiBase?: string
  rateLimitCheck?: RateLimitCheck
}

export async function listPosts(
  args: SocialListPostsArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: ListPostsDeps = {},
): Promise<SocialResult<SocialListPostsResult>> {
  if (typeof pageAccessToken !== 'string' || pageAccessToken.length === 0) {
    return {
      ok: false,
      error: {
        code: 'auth',
        message: 'Facebook Page access token is missing',
        retryable: false,
      },
    }
  }
  if (typeof args.targetAccountId !== 'string' || args.targetAccountId.length === 0) {
    return {
      ok: false,
      error: {
        code: 'provider_rejected',
        message: 'targetAccountId is required',
        retryable: false,
      },
    }
  }

  const gate = await checkRateLimit(ctx, ENDPOINT_LABEL, deps.rateLimitCheck)
  if (!gate.ok) return gate as SocialResult<SocialListPostsResult>

  const base = deps.graphApiBase ?? FB_PAGE_GRAPH_API_BASE
  const params = buildListPostsParams(args, COMPAT_POST_FIELDS)
  const url = `${base}/${encodeURIComponent(args.targetAccountId)}/posts`

  const response = await fetchPosts(url, params, pageAccessToken, ctx, deps.fetchImpl)
  if (!response.ok) {
    return {
      ok: false,
      error: await errorFromResponse(response, 'Facebook Graph posts failed'),
    }
  }

  const body = (await response.json().catch(() => null)) as FacebookPagePostsResponse | null
  if (!body || !Array.isArray(body.data)) {
    return {
      ok: false,
      error: {
        code: 'unknown',
        message: 'Facebook posts returned no data array',
        retryable: false,
      },
    }
  }

  return {
    ok: true,
    data: {
      posts: body.data.map((row) => toSocialProviderPost(row, args.targetAccountId)),
      nextPageToken: body.paging?.cursors?.after,
    },
  }
}

function buildListPostsParams(
  args: SocialListPostsArgs,
  fields: readonly string[],
): URLSearchParams {
  const params = new URLSearchParams({
    fields: fields.join(','),
    limit: String(clampLimit(args.limit)),
  })
  if (typeof args.pageToken === 'string' && args.pageToken.length > 0) {
    params.set('after', args.pageToken)
  }
  const since = parseUnixSeconds(args.since)
  if (since) params.set('since', String(since))
  const until = parseUnixSeconds(args.until)
  if (until) params.set('until', String(until))
  return params
}

async function fetchPosts(
  url: string,
  params: URLSearchParams,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  fetchImpl: typeof fetch | undefined,
): Promise<Response> {
  try {
    return await (fetchImpl ?? fetch)(
      `${url}?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${pageAccessToken}`,
        },
      },
    )
  } catch (err) {
    ctx.redactedLogger.error('listPosts network error', {
      error: err instanceof Error ? err.message : 'unknown',
    })
    return new Response(
      JSON.stringify({
        error: {
          code: 1,
          message: 'Network error talking to Facebook Graph',
          type: 'NetworkError',
        },
      }),
      { status: 502, headers: { 'Content-Type': 'application/json' } },
    )
  }
}

function toSocialProviderPost(
  row: FacebookPagePostRow,
  targetAccountId: string,
): SocialProviderPost {
  const out: SocialProviderPost = {
    providerPostId: row.id,
    targetAccountId,
  }
  if (typeof row.message === 'string') out.caption = row.message
  if (typeof row.link === 'string') out.linkUrl = row.link
  if (typeof row.permalink_url === 'string') out.providerUrl = row.permalink_url
  if (typeof row.created_time === 'string') {
    out.createdAt = row.created_time
    out.publishedAt = row.created_time
  }
  if (typeof row.is_published === 'boolean') out.isPublished = row.is_published

  const metrics: NonNullable<SocialProviderPost['metrics']> = {}
  if (typeof row.likes?.summary?.total_count === 'number') {
    metrics.likes = row.likes.summary.total_count
  }
  if (typeof row.comments?.summary?.total_count === 'number') {
    metrics.comments = row.comments.summary.total_count
  }
  if (typeof row.shares?.count === 'number') {
    metrics.shares = row.shares.count
  }
  if (Object.keys(metrics).length > 0) out.metrics = metrics

  out.metadata = {
    statusType: row.status_type,
    fullPicture: row.full_picture,
  }
  return out
}

function clampLimit(limit: number | undefined): number {
  if (!limit || !Number.isFinite(limit)) return DEFAULT_LIMIT
  return Math.max(1, Math.min(MAX_LIMIT, Math.floor(limit)))
}

function parseUnixSeconds(value: string | undefined): number | null {
  if (!value) return null
  const ms = Date.parse(value)
  if (!Number.isFinite(ms)) return null
  return Math.floor(ms / 1000)
}

async function checkRateLimit(
  ctx: SocialPostingCallCtx,
  endpoint: string,
  rateLimitCheck: RateLimitCheck | undefined,
): Promise<SocialResult<true>> {
  if (!rateLimitCheck) return { ok: true, data: true }
  const gate = await rateLimitCheck({
    instanceId: ctx.instanceId,
    endpoint,
  }, ctx)
  if (gate.allowed) return { ok: true, data: true }
  return {
    ok: false,
    error: {
      code: 'rate_limit',
      message: 'Facebook Page rate limit exceeded',
      retryable: true,
      retryAfterSeconds: gate.retryAfterSeconds,
    },
  }
}
